import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { Contact } from '../shared/types'

export type ContactInput = {
  account_id: string
  full_name: string
  title?: string
  email?: string
  phone?: string
  is_primary?: boolean
}

export type ContactUpdate = Partial<Omit<ContactInput, 'account_id'>>

export const useContacts = (accountId: string) =>
  useQuery({
    queryKey: ['contacts', accountId],
    queryFn: () => api.get<Contact[]>('/api/v1/contacts', { account_id: accountId }),
    enabled: !!accountId,
  })

export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ContactInput) => api.post<{ id: string }>('/api/v1/contacts', body),
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: ['contacts', v.account_id] }),
  })
}

export function useUpdateContact(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: ContactUpdate & { id: string }) => api.put(`/api/v1/contacts/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contacts', accountId] }),
  })
}

export function useDeleteContact(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/contacts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contacts', accountId] }),
  })
}
